/**
 * Lavelle Product Form & Variant Selector
 */

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.productJson = JSON.parse(this.querySelector('[data-product-json]')?.textContent || '{}');
    this.variantIdInput = this.querySelector('input[name="id"]');
    this.priceElement = document.querySelector('[data-product-price]');
    this.comparePriceElement = document.querySelector('[data-product-compare-price]');
    this.inventoryIndicator = document.querySelector('[data-inventory-indicator]');
    this.mainImage = document.querySelector('[data-main-product-image]');
    this.submitBtn = this.querySelector('[type="submit"]');

    this.bindEvents();
  }

  bindEvents() {
    // Option chip / swatch clicks
    this.querySelectorAll('[data-option-index]').forEach(optionWrap => {
      optionWrap.addEventListener('click', (e) => {
        const target = e.target.closest('[data-option-value]');
        if (!target) return;
        
        // Update selected state in UI
        const siblings = optionWrap.querySelectorAll('[data-option-value]');
        siblings.forEach(s => s.classList.remove('selected'));
        target.classList.add('selected');

        // Update option label display if present
        const labelDisplay = optionWrap.querySelector('[data-selected-label]');
        if (labelDisplay) {
          labelDisplay.textContent = target.dataset.optionValue;
        }

        this.onOptionChange();
      });
    });

    // Form submission via AJAX
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.onSubmit();
      });
    }

    // Quantity selectors (+ / -)
    const qtyWrap = this.querySelector('.quantity-selector');
    if (qtyWrap) {
      const input = qtyWrap.querySelector('.quantity-input');
      qtyWrap.querySelectorAll('.quantity-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          let val = parseInt(input.value || 1, 10);
          if (btn.dataset.change === 'minus') {
            val = Math.max(1, val - 1);
          } else {
            val = val + 1;
          }
          input.value = val;
        });
      });
    }

    // Thumbnail clicks to change main image
    document.querySelectorAll('[data-thumb-index]').forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        document.querySelectorAll('[data-thumb-index]').forEach(t => t.classList.remove('active'));
        thumb.classList.add('active');
        const newSrc = thumb.dataset.fullSrc;
        if (this.mainImage && newSrc) {
          this.mainImage.src = newSrc;
        }
      });
    });
  }

  getSelectedOptions() {
    const selected = [];
    this.querySelectorAll('[data-option-index]').forEach(wrap => {
      const activeChip = wrap.querySelector('[data-option-value].selected');
      if (activeChip) {
        selected.push(activeChip.dataset.optionValue);
      }
    });
    return selected;
  }

  onOptionChange() {
    const selectedOptions = this.getSelectedOptions();
    if (!this.productJson.variants) return;

    // Find matched variant
    const matchedVariant = this.productJson.variants.find(v => {
      return v.options.every((opt, idx) => opt === selectedOptions[idx]);
    });

    if (matchedVariant) {
      this.variantIdInput.value = matchedVariant.id;
      
      // Update Price
      if (this.priceElement) {
        const formattedPrice = (matchedVariant.price / 100).toFixed(2);
        const symbol = window.LavelleConfig?.currencySymbol || '€';
        this.priceElement.innerHTML = `${symbol}${formattedPrice}`;
      }

      if (this.comparePriceElement) {
        if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
          const compPrice = (matchedVariant.compare_at_price / 100).toFixed(2);
          const symbol = window.LavelleConfig?.currencySymbol || '€';
          this.comparePriceElement.innerHTML = `${symbol}${compPrice}`;
          this.comparePriceElement.style.display = 'inline';
        } else {
          this.comparePriceElement.style.display = 'none';
        }
      }

      // Update Inventory state
      if (this.inventoryIndicator) {
        const dot = this.inventoryIndicator.querySelector('.inventory-dot');
        const text = this.inventoryIndicator.querySelector('.inventory-text');
        if (matchedVariant.available) {
          dot.className = 'inventory-dot in-stock';
          text.textContent = 'In Stock — Ready to ship from atelier';
          if (this.submitBtn) {
            this.submitBtn.disabled = false;
            this.submitBtn.textContent = 'Add to Bag';
          }
        } else {
          dot.className = 'inventory-dot out-of-stock';
          text.textContent = 'Currently Sold Out';
          if (this.submitBtn) {
            this.submitBtn.disabled = true;
            this.submitBtn.textContent = 'Sold Out';
          }
        }
      }

      // Update Featured image if variant has image
      if (matchedVariant.featured_image && matchedVariant.featured_image.src && this.mainImage) {
        this.mainImage.src = matchedVariant.featured_image.src;
        // Also highlight matching thumbnail
        document.querySelectorAll('[data-thumb-index]').forEach(thumb => {
          if (thumb.dataset.fullSrc?.includes(matchedVariant.featured_image.src.split('?')[0])) {
            document.querySelectorAll('[data-thumb-index]').forEach(t => t.classList.remove('active'));
            thumb.classList.add('active');
          }
        });
      }

      // Update browser URL without reloading
      const url = new URL(window.location);
      url.searchParams.set('variant', matchedVariant.id);
      window.history.replaceState({}, '', url);

    } else {
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.textContent = 'Unavailable';
      }
    }
  }

  async onSubmit() {
    if (!this.submitBtn || this.submitBtn.disabled) return;
    
    const originalText = this.submitBtn.textContent;
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = 'Adding...';

    const formData = new FormData(this.form);

    if (window.LavelleCart) {
      await window.LavelleCart.addToCart(formData);
    } else {
      // Fallback
      await fetch('/cart/add.js', { method: 'POST', body: formData });
      window.location.href = '/cart';
    }

    this.submitBtn.disabled = false;
    this.submitBtn.textContent = originalText;
  }
}

customElements.define('product-form', ProductForm);
